import { Twitter, Instagram, Youtube, Mail, ArrowUpRight } from 'lucide-react'

interface FooterProps {
  onNavigate: (page: string) => void
}

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear()

  const socialLinks = [
    {
      label: 'Twitter',
      href: 'https://x.com/learningxperien',
      icon: <Twitter size={20} aria-hidden="true" />,
    },
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/learningxperiencelab/',
      icon: <Instagram size={20} aria-hidden="true" />,
    },
    {
      label: 'YouTube',
      href: 'https://www.youtube.com/@LearningXperienceLab',
      icon: <Youtube size={20} aria-hidden="true" />,
    },
  ]

  const navLinks = [
    { label: 'Expertise', href: '#expertise' },
    { label: 'About', href: '#about' },
    { label: 'Insights', href: '#insights' },
    { label: 'Contact', href: '#contact' },
  ]

  return (
    <footer
      className="bg-slate-900 text-slate-300 py-16"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="container-wide">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault()
                onNavigate('home')
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              className="inline-flex items-center gap-3 mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded-lg"
              aria-label="Learning Xperience Consulting - Home"
            >
              {/* Logo Icon */}
              <img
                src="/logoLXC.png"
                alt=""
                className="h-12 w-auto brightness-0 invert"
                aria-hidden="true"
              />
              {/* Company Name */}
              <div className="flex flex-col leading-tight">
                <span className="text-white font-bold text-lg tracking-tight">
                  Learning <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-amber">X</span>perience
                </span>
                <span className="text-slate-400 text-xs font-medium tracking-wide uppercase">
                  Consulting
                </span>
              </div>
            </a>
            <p className="text-slate-400 max-w-md mb-6">
              Transform higher education with innovative EdTech solutions. We help
              organizations create engaging, accessible learning experiences that
              put learners first.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 hover:bg-brand-blue flex items-center justify-center text-slate-400 hover:text-white transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                  aria-label={`Follow us on ${link.label}`}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Navigation Column */}
          <nav aria-label="Footer navigation">
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault()
                      onNavigate(link.href)
                    }}
                    className="text-slate-400 hover:text-white transition-colors focus:outline-none focus-visible:text-brand-blue"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact Column */}
          <div>
            <h3 className="text-white font-semibold mb-4">Get in Touch</h3>
            <a
              href="mailto:gio@learningxperience.tech"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4 focus:outline-none focus-visible:text-brand-blue"
            >
              <Mail size={18} aria-hidden="true" />
              gio@learningxperience.tech
            </a>
            <div className="mt-6">
              <a
                href="https://www.linkedin.com/in/giovanniduarte"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-brand-blue hover:text-brand-amber transition-colors focus:outline-none focus-visible:underline"
              >
                Connect on LinkedIn
                <ArrowUpRight size={16} aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} Learning Xperience Consulting. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            Designed with learners in mind.
          </p>
        </div>
      </div>
    </footer>
  )
}
